import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Typography } from "@mui/material";

/** One control, one height, whatever kind of control it is. */
const FIELD_HEIGHT = 40;

// The row keeps the height of an input and centres whatever sits in it, so a
// control shorter than a text field stays on the same baseline. The height of a
// toggle group is its own `size` to choose, not this row's to impose.
const ROW_HEIGHT = {
  display: "flex",
  alignItems: "center",
  minHeight: FIELD_HEIGHT,
  width: "100%",
};

/**
 * A text field the same height as a toggle group beside it.
 *
 * The theme gives every `TextField` `margin="dense"`, which adds 8px above and
 * 4px below the control, outside its height. The input was the right size all
 * along; its wrapper was 12px taller, which is what stood proud of the toggles.
 */
export const FIELD_INPUT = {
  margin: 0,
  // The picker wraps its two fields in their own dense FormControls, so the
  // margin has to be cleared on the descendants as well as on the root.
  "& .MuiFormControl-root": { margin: 0 },
  "& .MuiInputBase-root, & .MuiPickersInputBase-root": {
    height: FIELD_HEIGHT,
    boxSizing: "border-box",
  },
};

/**
 * One labelled control. Every field of the form is built this way, inputs
 * included: their own MUI labels are turned off so that a caption, a toggle
 * group and a date range all sit on the same two lines and line up.
 */
const Field = ({
  label,
  width,
  hint,
  children,
}: {
  label: string;
  width?: number;
  /** A translation key explaining what the control decides, under it. */
  hint?: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      // A hint spans the row and wraps; the control keeps its own width
      // underneath. Sizing the field to the hint instead pushed it past the
      // dialog and put a horizontal scrollbar under the whole form.
      width: hint ? "100%" : width,
      // A field keeps its width in a wrapping row. Letting it shrink is what
      // ran the weekday chips over the date pair and squeezed the hotel
      // dropdown down to "P...".
      flexShrink: 0,
    }}
  >
    <Typography
      color="text.secondary"
      sx={{ fontSize: "0.8125rem", fontWeight: 700 }}
    >
      <FormattedMessage id={label} />
    </Typography>
    <Box sx={{ ...ROW_HEIGHT, width: width ?? "100%" }}>{children}</Box>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        <FormattedMessage id={hint} />
      </Typography>
    )}
  </Box>
);

export default Field;
